<script setup lang="ts">
import { useVModel } from '@vueuse/core'
import {
  ComboboxInput,
  TagsInputInput,
  TagsInputItem,
  TagsInputItemDelete,
  TagsInputItemText,
  TagsInputRoot
} from 'reka-ui'
import type { HTMLAttributes } from 'vue'
import { BASE_CLASS, BASE_INVALID_CLASS } from '../Input/variant'

const props = withDefaults(
  defineProps<{
    modelValue?: string[]
    defaultValue?: string[]
    modelValueQuery?: string
    class?: HTMLAttributes['class']
    invalid?: boolean
    placeholder?: string
    spaceToAdd?: boolean
    withOption?: boolean
    maxLength?: number
    tagValidation?: (tag: string) => boolean
    duplicate?: boolean
    max?: number
  }>(),
  {
    modelValue: undefined,
    defaultValue: undefined,
    modelValueQuery: undefined,
    class: undefined,
    placeholder: 'Add a tag...',
    withOption: false,
    maxLength: undefined,
    tagValidation: (tag: string) => {
      return tag.trim().length > 3
    },
    max: 99
  }
)

const emits = defineEmits<{
  (e: 'update:modelValue' | 'update:modelValueQuery', value: string[]): void
}>()

const modelValue = useVModel(props, 'modelValue', emits, {
  defaultValue: props.defaultValue
})

const modelValueQuery = useVModel(props, 'modelValueQuery', emits)

const delimiter = computed(() => {
  return props.spaceToAdd ? /[ ,;\t\n\r]+/ : /[,;\t\n\r]+/
})

const tagsInput = useTemplateRef('tagsInput')

defineExpose({
  focus: () => {
    tagsInput.value?.$el?.focus?.()
  }
})

const mergeAndIgnoreCase = (arr: string[]) => {
  const seen = new Set()
  const mergedArray = []

  for (const item of arr) {
    const lowerCaseItem = item.toLowerCase()
    if (!seen.has(lowerCaseItem)) {
      seen.add(lowerCaseItem)
      mergedArray.push(item)
    }
  }

  return mergedArray
}

const validate = (payload: string[]) => {
  const filteredTags = payload.filter((tag) => props.tagValidation(tag))
  let result = props.duplicate ? filteredTags : mergeAndIgnoreCase(filteredTags)

  // Limit to max number of tags
  if (result.length > props.max) {
    result = result.slice(0, props.max)
  }

  return (modelValue.value = result)
}
</script>

<template>
  <TagsInputRoot
    v-model="modelValue"
    :class="
      cn(
        BASE_CLASS,
        'flex gap-1.5 data-[focused]:border-primary data-[focused]:ring-1 data-[focused]:ring-primary',
        {
          [BASE_INVALID_CLASS]: props.invalid,
          'py-1.5 px-1.5': (modelValue?.length ?? 0) > 0 && !props.withOption
        },
        props.class
      )
    "
    :delimiter="delimiter"
    add-on-paste
    :duplicate="duplicate"
    @update:model-value="validate"
  >
    <TagsInputItem
      v-for="item in modelValue"
      :key="item"
      :value="item"
      class="flex items-center justify-center shrink-0 gap-2 bg-surface-container-high aria-[current=true]:bg-outline-variant rounded-full py-1.5 px-2"
    >
      <TagsInputItemText class="text-xs break-all" />
      <TagsInputItemDelete class="p-0 bg-transparent cursor-pointer">
        <Icon name="material-symbols:close" size="16px" />
      </TagsInputItemDelete>
    </TagsInputItem>

    <template v-if="(modelValue?.length ?? 0) < props.max">
      <ComboboxInput v-if="withOption" v-model="modelValueQuery" as-child>
        <TagsInputInput
          ref="tagsInput"
          :placeholder="placeholder"
          class="text-sm focus:outline-none shrink-0 lg:flex-1 bg-transparent placeholder:text-outline"
          :max-length="maxLength"
        />
      </ComboboxInput>

      <TagsInputInput
        v-else
        ref="tagsInput"
        :placeholder="placeholder"
        class="text-sm focus:outline-none shrink-0 lg:flex-1 bg-transparent placeholder:text-outline"
        :max-length="maxLength"
      />
    </template>
  </TagsInputRoot>
</template>
